import * as THREE from 'three';
import type { PlaceInst } from './types';

/**
 * scenery/laundryNapoli.ts — the washing strung across Spaccanapoli's alleys.
 *
 * WHY THIS LIVES HERE AND NOT IN builders.ts: `laundry` is a Napoli-only
 * scenery array (types.ts says so, and zones/napoli.ts is its only writer), so
 * this is a city-specific builder that merely happened to sit in the shared
 * file. It is owned by the Napoli round; builders.ts is untouched.
 *
 * WHAT CHANGED (QA: alley-chase — the lines read as taut wires with flat
 * garments pinned dead-square to them):
 *  - THE LINE IS A REAL CATENARY. It used to be ONE box scaled to the full
 *    span — a dead-straight rod. It is now SEG segments whose ends sit on the
 *    sag curve, each pitched to the local slope, so the rope droops the way a
 *    loaded line actually does. The sag the cloths already used (a parabola,
 *    (1 − t²)·sag) is now shared by the rope and the washing, so garments hang
 *    FROM the rope instead of near it.
 *  - GARMENTS TILT. Every piece gets a per-instance roll (a shirt on a line is
 *    never plumb) and a small yaw, both from an index hash so the variety is
 *    deterministic and costs no rng draw.
 *
 * All of it is still two InstancedMeshes — the rope's segment count is the only
 * added cost, and the whole city's washing is a handful of lines.
 */

const CLOTH_COLORS = [0xded6c4, 0x4a90c8, 0xd8563a, 0xe0c838, 0x6aa84f, 0xd87aa8, 0xc8cfd4, 0xd9a05c];

/**
 * ONE HANGING GARMENT (QA: launcher-back-orbit — the big cream piece by the
 * launcher read as a flat white BOARD hung over the road, out of step with the
 * catenary rope beside it). A box can only ever be a board, so the garment is
 * now a real draped sheet:
 *  - pinned at TWO PEGS: the top edge bellies gently between them and the two
 *    corners flop over outside them (that silhouette alone kills the board);
 *  - a soft S-shaped BILLOW across the width that deepens toward the hem, so
 *    the sun rakes across it and one side is always in shade;
 *  - smooth normals (the material's flatShading is off) so the curve shades as
 *    cloth rather than as facets.
 * Still ONE instanced geometry for the whole city's washing — the variety is
 * per-instance SCALE (width and drop), from the same index hash as the tilt.
 */
const CLOTH_W = 1.15;
const CLOTH_H = 1.4;
/** Peg positions as a fraction of the half-width. */
const PEG = 0.4;

function clothGeometry(): THREE.BufferGeometry {
  const NX = 8;
  const NY = 6;
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  for (let j = 0; j <= NY; j++) {
    const v = j / NY; // 0 at the pegs, 1 at the hem
    for (let i = 0; i <= NX; i++) {
      const u = i / NX;
      const n = (u - 0.5) * 2; // −1..1 across
      const a = Math.abs(n);
      // The top edge: a shallow belly between the pegs, a real flop outside.
      const sag =
        a < PEG
          ? 0.06 * (1 - (n / PEG) ** 2)
          : 0.2 * ((a - PEG) / (1 - PEG)) ** 1.5;
      // The billow: an S across the width, growing toward the hem, plus a
      // second finer fold so the piece never reads as one clean cylinder.
      const z =
        Math.sin(n * 2.4) * 0.09 * (0.22 + v * 0.95) + Math.sin(n * 5.3 + 1.3) * 0.028 * v;
      pos.push(
        n * CLOTH_W * 0.5,
        CLOTH_H * 0.5 - v * CLOTH_H - sag * (1 - v * 0.5),
        z,
      );
      uv.push(u, 1 - v);
      if (i > 0 && j > 0) {
        const k = j * (NX + 1) + i;
        const kp = k - (NX + 1);
        idx.push(kp - 1, kp, k - 1, kp, k, k - 1);
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/** Cloths hang at ±offsets only — the centre stays clear so the camera never
 *  flies straight through a sheet. */
const SPOTS = [-0.77, -0.38, 0.38, 0.77];
/** Rope segments per line. 8 is where the droop stops reading as a polyline. */
const SEG = 8;
/** Peak sag at mid-span, in world units. */
const SAG = 0.55;

/** Deterministic 0..1 from an integer (no rng draw). */
function hash01(i: number): number {
  const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/** Sag depth below the posts at normalised half-span t ∈ [−1, 1]. */
function sagAt(t: number): number {
  return (1 - t * t) * SAG;
}

export function buildNapoliLaundry(insts: PlaceInst[]): THREE.InstancedMesh[] {
  const lineMat = new THREE.MeshStandardMaterial({ color: 0xd8d2c2, roughness: 1 });
  const clothMat = new THREE.MeshStandardMaterial({
    // SMOOTH, not flat: the drape's own curvature is the shading now, and
    // faceting a 1 u sheet just rebuilds the board read in triangles.
    roughness: 0.95,
    side: THREE.DoubleSide,
  });
  const lines = new THREE.InstancedMesh(
    new THREE.BoxGeometry(1, 0.09, 0.09),
    lineMat,
    insts.length * SEG,
  );
  const cloths = new THREE.InstancedMesh(clothGeometry(), clothMat, insts.length * SPOTS.length);
  const M = new THREE.Matrix4();
  const P = new THREE.Vector3();
  const S = new THREE.Vector3();
  const Q = new THREE.Quaternion();
  const E = new THREE.Euler();
  const C = new THREE.Color();

  insts.forEach((b, i) => {
    const span = b.s ?? 40;
    const cosr = Math.cos(b.rotY);
    const sinr = Math.sin(b.rotY);
    // Along the line: world offset for a normalised half-span t.
    const at = (t: number): [number, number, number] => {
      const lx = t * span * 0.5;
      return [b.x + lx * cosr, b.y - sagAt(t), b.z - lx * sinr];
    };

    // ---- The rope: SEG segments chasing the sag curve.
    for (let k = 0; k < SEG; k++) {
      const t0 = -1 + (2 * k) / SEG;
      const t1 = -1 + (2 * (k + 1)) / SEG;
      const [x0, y0, z0] = at(t0);
      const [x1, y1, z1] = at(t1);
      const dx = x1 - x0;
      const dy = y1 - y0;
      const dz = z1 - z0;
      const flat = Math.hypot(dx, dz);
      // The unit box runs along local X, so yaw it onto the span and pitch it
      // (about local Z) down the slope of this segment.
      E.set(0, b.rotY, Math.atan2(dy, flat), 'YXZ');
      Q.setFromEuler(E);
      lines.setMatrixAt(
        i * SEG + k,
        M.compose(
          P.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2),
          Q,
          S.set(Math.hypot(flat, dy), 1, 1),
        ),
      );
    }

    // ---- The washing: hung FROM the rope, and never plumb.
    SPOTS.forEach((t, k) => {
      const [wx, wy, wz] = at(t);
      const h = hash01(i * 17 + k * 5);
      const roll = (h - 0.5) * 0.5; // a shirt on a line is never square
      const yaw = b.rotY + (hash01(i * 23 + k * 7) - 0.5) * 0.6;
      E.set(0, yaw, roll, 'YXZ');
      Q.setFromEuler(E);
      const drop = 0.68 + h * 0.22; // pegged at slightly different heights
      // Size variety, so a line is a mixed wash and never four copies of the
      // same rectangle (the "oversized sheet" read). Width and drop come from
      // DIFFERENT hashes — a wide piece is not automatically a long one.
      const wide = 0.78 + hash01(i * 41 + k * 13) * 0.42;
      cloths.setMatrixAt(
        i * SPOTS.length + k,
        M.compose(P.set(wx, wy - drop, wz), Q, S.set(wide, 0.8 + h * 0.28, 1)),
      );
      cloths.setColorAt(i * SPOTS.length + k, C.set(CLOTH_COLORS[(i + k) % CLOTH_COLORS.length]));
    });
  });
  lines.instanceMatrix.needsUpdate = true;
  cloths.instanceMatrix.needsUpdate = true;
  if (cloths.instanceColor) cloths.instanceColor.needsUpdate = true;
  return [lines, cloths];
}
